import React from "react";
import { Card } from "@/components/ui/card";
import CapacityBar from "@/components/inventory/CapacityBar";
import { MapPin, Package, WarningCircle, Info } from "@phosphor-icons/react";

/**
 * Location detail view — the authoritative on-hand snapshot for one bin.
 * Renders the location metadata + capacity bar + items on hand table.
 *
 * Two edge cases:
 *   • location: null but items present → "code not in location registry" note,
 *     still render the stock rows (per spec, they're the source of truth).
 *   • No items → empty-state message. Still show location metadata.
 */
export default function LocationDetail({ data, loading, error, onRetry }) {
  if (error) {
    return (
      <Card className="p-6 border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800" data-testid="location-detail-error">
        <div className="flex items-start gap-3">
          <WarningCircle size={24} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-black text-lg">Base44 unavailable</div>
            <p className="text-sm text-red-800 dark:text-red-200 mt-1">{error}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-3 px-3 py-1.5 rounded-md border border-red-400 bg-white dark:bg-red-950/40 text-red-800 dark:text-red-200 text-xs font-bold uppercase tracking-[0.15em]"
                data-testid="location-detail-retry"
              >
                Try refresh
              </button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="p-6" data-testid="location-detail-loading">
        <div className="text-sm text-zinc-500">Loading location…</div>
      </Card>
    );
  }

  if (!data) return null;

  const loc = data.location; // may be null for unregistered codes
  const items = Array.isArray(data.items) ? data.items : [];
  const itemCount = data.item_count ?? items.length;
  const totalQty = data.total_quantity ?? items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  const requestedCode = data.location_code;

  return (
    <div className="space-y-4" data-testid="location-detail">
      {/* Metadata */}
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <MapPin size={20} weight="bold" className="text-zinc-500 flex-shrink-0" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">
                Location
              </div>
            </div>
            <h2 className="font-mono font-black text-2xl sm:text-3xl mt-1 break-all" data-testid="location-detail-code">
              {requestedCode}
            </h2>
            {loc ? (
              <>
                <div className="text-lg text-zinc-800 dark:text-zinc-200 mt-1" data-testid="location-detail-name">
                  {loc.location_name || "—"}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mt-1">
                  {loc.zone || "—"}
                  {loc.location_type ? ` · ${loc.location_type}` : ""}
                  {loc.is_active === false ? " · INACTIVE" : ""}
                </div>
              </>
            ) : (
              <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 rounded-md px-2.5 py-2">
                <Info size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                <span data-testid="location-detail-unregistered">
                  This code isn't in the location registry. Stock rows below are still authoritative.
                </span>
              </div>
            )}
          </div>
          {loc && (
            <div className="w-full sm:w-64 flex-shrink-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">
                Capacity
              </div>
              <CapacityBar location={loc} />
            </div>
          )}
        </div>
      </Card>

      {/* Items on hand */}
      <Card className="p-5">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Package size={18} weight="bold" className="text-zinc-500" />
            <h3 className="font-heading font-black text-lg">Items on hand</h3>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold" data-testid="items-on-hand-summary">
            {itemCount} SKU{itemCount === 1 ? "" : "s"} · {totalQty} units
          </div>
        </div>

        {!items.length ? (
          <div className="text-sm text-zinc-500 italic text-center py-8" data-testid="items-empty">
            No items on hand at this location.
          </div>
        ) : (
          <div className="border border-zinc-200 dark:border-zinc-800 rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="items-on-hand-table">
              <thead className="bg-zinc-50 dark:bg-zinc-900 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                <tr>
                  <th className="text-left py-2 px-3">SKU</th>
                  <th className="text-left py-2 px-3">Product</th>
                  <th className="text-right py-2 px-3">Qty</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr
                    key={it.product_id || `${it.product_sku}-${i}`}
                    className="border-t border-zinc-200 dark:border-zinc-800"
                    data-testid={`item-on-hand-${it.product_sku || i}`}
                  >
                    <td className="py-2 px-3 font-mono font-semibold whitespace-nowrap">
                      {it.product_sku || "—"}
                    </td>
                    <td className="py-2 px-3 text-zinc-800 dark:text-zinc-200">
                      {it.product_name || "—"}
                    </td>
                    <td className="py-2 px-3 text-right font-mono font-bold">
                      {it.quantity ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
